/*
 * -----------------------------------------------------------------------
 *  <copyright file="audit-log/change-grid.tsx" company="Triumph Research Intelligence">
 *      Copyright (C) 2023 by Triumph Research Intelligence. All rights reserved.
 *  </copyright>
 * -----------------------------------------------------------------------
 */

import React from 'react';
import Moment from 'react-moment';
import { HeaderThElement } from '@progress/kendo-react-data-tools';
import { Grid, GridColumn as Column, GridCustomHeaderCellProps } from '@progress/kendo-react-grid';
import AuditItemTypes from './definitions/item-types';
import { getFieldChangeItems } from './service';
import { useTranslation } from 'hooks';
import { AuditActionType, AuditDataType } from 'types/graphql-global-types';

interface IDataItem {
    field: string;
    oldValue: string;
    newValue: string;
}

const ColumnHeaderCell = ({ children, title, thProps }: GridCustomHeaderCellProps) => {
    return (
        <HeaderThElement columnId={thProps?.columnId || ''} {...thProps} className={thProps?.className}>
            <div className="k-link !k-cursor-default">
                <span>{title}</span>
            </div>
        </HeaderThElement>
    );
};

const FieldChangeGrid = ({ items }: any) => {
    const { t } = useTranslation();

    const Cell = ({ fieldType, value }: { fieldType: string; value: string }) => {
        let content = <>{value}</>;

        if (fieldType === 'Logo' && value && value !== 'None') {
            content = <img src={value} alt="Logo" style={{ height: 150, width: 150 }} />;
        }

        return <td style={{ whiteSpace: 'pre-line' }}>{content}</td>;
    };

    return (
        <Grid data={items} total={items.length}>
            <Column title="Field" field="field" cells={{ headerCell: ColumnHeaderCell }} />
            <Column
                title={t('auditLog:oldValue')}
                field="oldValue"
                cells={{
                    headerCell: ColumnHeaderCell,
                    data: ({ dataItem }: { dataItem: IDataItem }) => (
                        <Cell fieldType={dataItem.field} value={dataItem.oldValue} />
                    ),
                }}
            />
            <Column
                title={t('auditLog:newValue')}
                field="newValue"
                cells={{
                    headerCell: ColumnHeaderCell,
                    data: ({ dataItem }: { dataItem: IDataItem }) => (
                        <Cell fieldType={dataItem.field} value={dataItem.newValue} />
                    ),
                }}
            />
        </Grid>
    );
};

const RecordChangeGrid = React.memo(
    ({ dataType, newItem, oldItem, newRecord, oldRecord, record, additionalInfo }: any) => {
        const { t } = useTranslation();
        // TODO: remove nested component anti-pattern
        const getChangeGrid = () => {
            // Only do this for deleted items that are not Obs or Action related.
            if (record.action === AuditActionType.DATA_EXPORT) {
                if (dataType === AuditDataType.PBI_REPORT) return <div>{t('auditLog:reportDownloaded')}</div>;
                if (dataType === AuditDataType.DATASET_IMPORT_LOG)
                    return <div>{t('auditLog:datasetImportLogDownloaded')}</div>;
                if (dataType === AuditDataType.DATA_QUALITY_REPORT) return <div>{t('auditLog:reportDownloaded')}</div>;
                if (dataType === AuditDataType.CM_INTEGRATION_CONFIG)
                    return <div>{t('auditLog:cmIntegrationConfigDownload')}</div>;
                return <div>{t('auditLog:dataExport')}</div>;
            }
            if (record.action === AuditActionType.ADD && dataType === AuditDataType.CM_INTEGRATION_CONFIG)
                return <div>{t('auditLog:cmIntegrationConfigUpload')}</div>;
            if (
                !newRecord ||
                ((record.action === AuditActionType.DELETE || newRecord.D) &&
                    dataType !== AuditDataType.RISK_OBSERVATION &&
                    dataType !== AuditDataType.RISK_ACTION &&
                    dataType !== AuditDataType.CENTRAL_MONITORING_OBSERVATION &&
                    dataType !== AuditDataType.CENTRAL_MONITORING_OBSERVATION_ACTION)
            )
                return <div>{t('auditLog:recordDeleted')}</div>;
            if (record.action === AuditActionType.DATA_EXPORT) {
                if (dataType === AuditDataType.PBI_REPORT) return <div>{t('auditLog:reportDownloaded')}</div>;
                return <div>{t('auditLog:dataExport')}</div>;
            }

            const item = (AuditItemTypes as any)[dataType];
            return (
                item?.grid({ oldRecord, newRecord, oldItem, newItem, additionalInfo, record }) ?? (
                    <FieldChangeGrid items={getFieldChangeItems(oldRecord, newRecord)} />
                )
            );
        };

        return (
            <div style={{ marginTop: 20 }}>
                <h3>
                    {!!record && (
                        <Moment format="DD-MMM-YYYY HH:mm:ss UTC" utc>
                            {record.created}
                        </Moment>
                    )}{' '}
                    by {record?.userFirstName} {record?.userLastName} ({t(`auditLog:${record?.action}`)})
                </h3>
                {getChangeGrid()}
                <br />
                <br />
            </div>
        );
    },
);

export { FieldChangeGrid };

export default RecordChangeGrid;
